import { DashboardRouter } from "module/dashboard/DashboardRouter";
import { Suspense, useEffect, useMemo } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { LandingRouter } from "module/landing/LandingRouter";
import { PlatformRouter } from "module/platform/PlatformRouter";
import { ProfileRouter } from "module/profile/ProfileRouter";
import Spinner from "module/common/component/display/Loading/Loading";
import { WalletRouter } from "module/wallet/WalletRouter";
import { useLoad } from "module/common/hooks/useLoad";
import { useRecoilValue } from "recoil";
import { AdminRole, BrokerRole, DeveloperRole, authState } from "module/auth/AuthState";
import { AuthRouter } from "module/auth/AuthRouter";
import { DrawerProvider } from "module/common/hooks/useDrawer";
import { UseLogout } from "module/auth/query/useLogout";
import IdleTimer from "./utils/idle-timer";
import { PlatformGuestRouter, PlatformGuestRoutes } from "module/platform/PlatformGuestRouter";
import { BrokerRouter } from "module/broker/BrokerRouter";
import { lazy } from "react";

const WhatsAppConnectSuccess = lazy(() => import("module/broker/component/WhatsAppConnectSuccess/WhatsAppConnectSuccess"));
const WhatsAppConnectFailed = lazy(() => import("module/broker/component/WhatsAppConnectFailed/WhatsAppConnectFailed"));

const ScrollToTop: React.FC = () => {
    const { pathname } = useLocation();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    return null;
};

const Router = (): JSX.Element => {
    const isLoading = useLoad();
    const state = useRecoilValue(authState);

    useEffect(() => {
        if (!state.isLogged) {
            return;
        }

        const timer = new IdleTimer({
            timeout: 5 * 60, //expire after 5 minutes
            onTimeout: () => {
                const isDevelopment = process.env.NODE_ENV === "development";
                if (state.isLogged && !isDevelopment) UseLogout();
            },
            onExpired: () => {
                // do something if expired on load
            },
        });

        return () => {
            timer.cleanUp();
        };
    }, [state.isLogged]);

    const isAdmin = useMemo(() => {
        return Object.values(AdminRole).includes(state.role as AdminRole);
    }, [state.role]);

    const isBroker = useMemo(() => {
        return state.role === BrokerRole.BROKER;
    }, [state.role]);

    const isDeveloper = useMemo(() => {
        return state.role === DeveloperRole.DEVELOPER;
    }, [state.role]);

    const getMainRouter = () => {
        if (isAdmin) return <>{DashboardRouter}{BrokerRouter}{PlatformRouter}</>;
        if (isBroker) return <>{BrokerRouter}{PlatformRouter}</>;
        if (isDeveloper) return PlatformRouter;
        return PlatformRouter;
    };

    return (
        <BrowserRouter basename={process.env.PUBLIC_URL}>
            <ScrollToTop />
            <DrawerProvider>
                <Suspense fallback={<Spinner />}>
                    {isLoading ? (
                        <Spinner />
                    ) : (
                        <Routes>
                            {state.isLogged && ProfileRouter}
                            {state.isLogged && getMainRouter()}
                            {state.isLogged && ProfileRouter}
                            {state.isLogged && WalletRouter}
                            {!state.isLogged && LandingRouter}
                            {!state.isLogged && PlatformGuestRouter}
                            {AuthRouter}
                            <Route path={PlatformGuestRoutes.WHATSAPP_CONNECT_SUCCESS} element={<WhatsAppConnectSuccess />} />
                            <Route path={PlatformGuestRoutes.WHATSAPP_CONNECT_FAILED} element={<WhatsAppConnectFailed />} />
                            <Route path="/investor" element={<Navigate to="/" replace />} />
                            <Route path="*" element={<Navigate to={!state.isLogged ? "/" : isAdmin ? "/" : isBroker ? "/broker/dashboard" : isDeveloper ? "/" : "/"} />} />
                        </Routes>
                    )}
                </Suspense>
            </DrawerProvider>
        </BrowserRouter>
    );
};

export default Router;
